import { Injectable, NgZone, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  Subscription,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService, ApiResponse } from './api.service';
import { EncryptionService } from './encryption.service';
import { AccessRightService } from './access-right.service';
import { MasterDataCacheService } from './master-data-cache.service';
import { AccountCompanyService } from './account-company.service';
import {
  ACCOUNTING_COMPANY_OPEN_SESSION_KEY,
  ACCOUNTING_COMPANY_SESSION_KEY,
  DEFAULT_ACCOUNTING_COMPANY_ID,
} from '../constants/accounting-company.constants';
import { ToastService } from '../../shared/toast/toast.service';
import { SessionExpiryService } from '../../shared/session-expiry-modal/session-expiry.service';

/** Per-module permissions from login (keyed by AppModuleName). */

export interface AccessRightDto {
  appModuleName: string;
  /** @deprecated Legacy GUID; use appModuleName for checks. */
  moduleId?: string;
  /** @deprecated Use appModuleName. Kept for encrypted storage migration. */
  moduleName?: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport?: boolean;
}

/** App module that gates accounting Alter flows (dbo.AppModules.DisplayText: Edit Accounting). */
export const ACCOUNTING_EDIT_MODULE_NAME = 'AccountingEdit';

/** App module that gates voucher entry create/alter/delete (dbo.AppModules.DisplayText: Voucher Entry). */
export const ACCOUNTING_VOUCHER_ENTRY_MODULE_NAME = 'AccountingVoucherEntry';

/** Route path to AppModuleName (matches dbo.AppModules.AppModuleName). */

export const MODULE_NAMES_BY_PATH: Record<string, string> = {
  '/access-rights': 'AccessRights',
  '/users': 'Users',
  '/company-details': 'Company Details',
  '/module-management': 'ModuleManagement',
  '/categories': 'Category',
  // Solvix Salesmen / Purity masters retired from UI — use Accounts masters instead.
  // '/salesmen': 'Salesmen',
  // '/purities': 'Purity',
  '/daily-metal-rates': 'DailyMetalRates',
  '/audit-logs': 'AuditLogs',
  '/item-wise-profit-loss': 'ItemWiseProfitLoss',
  '/stock-report': 'StockReport',
  '/label-generation': 'LabelGeneration',
  '/accounting': 'AccountingGroupMaster',
  '/accounting/masters/create': 'AccountingGroupMaster',
  '/accounting/masters/alter': ACCOUNTING_EDIT_MODULE_NAME,
  '/accounting/group/create': 'AccountingGroupMaster',
  '/accounting/group/alter': ACCOUNTING_EDIT_MODULE_NAME,
  '/accounting/group-master': 'AccountingGroupMaster',
  '/accounting/ledger/create': 'AccountingLedgerMaster',
  '/accounting/ledger/alter': ACCOUNTING_EDIT_MODULE_NAME,
  '/accounting/vouchers': 'AccountingVouchers',
  '/accounting/vouchers/estimates': ACCOUNTING_VOUCHER_ENTRY_MODULE_NAME,
  '/accounting/vouchers/create': ACCOUNTING_VOUCHER_ENTRY_MODULE_NAME,
  '/accounting/vouchers/alter': ACCOUNTING_VOUCHER_ENTRY_MODULE_NAME,
  '/accounting/account-books/register': 'AccountingVouchers',
  '/accounting/day-book': 'AccountingVouchers',
  '/accounting/balance-sheet': 'AccountingVouchers',
  '/accounting/profit-loss': 'AccountingVouchers',
  '/accounting/stock-summary': 'AccountingVouchers',
  '/accounting/godown-summary': 'AccountingVouchers',
  '/accounting/company/create': 'AccountingCompanyMaster',
  '/accounting/company/alter': ACCOUNTING_EDIT_MODULE_NAME,
  '/accounting/stock-item-vouchers': 'AccountingVouchers',
  '/accounting/trial-balance': 'AccountingVouchers',
  '/accounting/account-books': 'AccountingVouchers',
  '/accounting/more-reports': 'AccountingVouchers',
  '/accounting/exception-reports': 'AccountingVouchers',
};


/** Routes that are not AppModules but always visible (e.g. dashboard). */
const UNGATED_VIEW_PATHS = new Set(['/dashboard']);

/** Normalize module keys for comparison (case-insensitive, ignore spaces). */
export function normalizeModuleKey(name: string | null | undefined): string {
  return (name ?? '').toLowerCase().replace(/\s+/g, '');
}
export interface LoginResponse {
  userId: string;
  userName: string;
  emailId: string;
  mobileNo: string;
  /** dbo.Role.RoleId */
  userRole: number | string;
  /** dbo.Role.Role name (not AccessRights). */
  userRoleName?: string;
  token: string;
  refreshToken: string;
  accessTokenExpiresAtUtc?: string;
  refreshTokenExpiresAtUtc: string;
  accessRights: AccessRightDto[];
  /** Resolved recent Accounts company (active company or default). */
  lastAccountCompanyId?: string;
}
export interface LoginRequest {
  userName: string;
  password: string;
}

const STORAGE_KEY = 'sj_auth';

export interface AuthStoragePayload {
  token: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  /** Module permissions (not in JWT — stored beside the token). */
  accessRights?: AccessRightDto[];
  /**
   * Built from JWT claims on read. Optional cached copy from login for email/mobile
   * until next token issue.
   */
  user: {
    userId: string;
    userName: string;
    emailId: string;
    mobileNo: string;
    /** dbo.Role.RoleId */
    userRole: number | string;
    /** dbo.Role name */
    userRoleName?: string;
    accessRights: AccessRightDto[];
  };
}

/**
 * Auth session in localStorage (shared across tabs / duplicates).
 * sessionStorage kept in sync for legacy reads only.
 */
interface JwtSessionStore {
  token: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  /** Stored beside the token so reload / duplicate tab keeps permissions. */
  accessRights?: AccessRightDto[];
}

interface JwtAccessClaims {
  exp?: number;
  sub?: string;
  unique_name?: string;
  name?: string;
  email?: string;
  Email?: string;
  MobileNo?: string;
  UserRole?: string | number;
  UserRoleName?: string;
  role?: string | number;
  /** .NET ClaimTypes.NameIdentifier often serializes as this URI key */
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiService = inject(ApiService);
  private encryption = inject(EncryptionService);
  private accessRightService = inject(AccessRightService);
  private masterDataCache = inject(MasterDataCacheService);
  private accountCompanyService = inject(AccountCompanyService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private sessionExpiryService = inject(SessionExpiryService);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private sessionWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionCheckInterval: ReturnType<typeof setInterval> | null = null;
  private routerSubscription: Subscription | null = null;
  private handlingSessionExpiry = false;
  /** Single in-flight Auth/refresh — avoids parallel 401s when many API calls retry at once. */
  private refreshInFlight$: Observable<boolean> | null = null;
  /** Module permissions — memory first; mirrored in sessionStorage for reload. */
  private memoryAccessRights: AccessRightDto[] = [];
  private readonly tabId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;

  private static readonly SESSION_POLL_MS = 15_000;
  /** Silently renew access JWT this far before expiry (keeps 24h sessions alive). */
  private static readonly SILENT_REFRESH_LEAD_MS = 5 * 60_000;
  /** Only one tab should call /Auth/refresh at a time (rotation race). */
  private static readonly REFRESH_LOCK_KEY = 'sj_auth_refresh_lock';
  private static readonly REFRESH_LOCK_MS = 20_000;
  private static readonly PEER_REFRESH_WAIT_MS = 22_000;
  /** Legacy cross-tab mirror from earlier session fix — migrated then removed. */
  private static readonly LEGACY_AUTH_SYNC_KEY = 'sj_auth_sync';
  private readonly sessionWarningMs =
    environment.sessionWarningSecondsBeforeExpiry * 1_000;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.migrateLegacyAuthStorage();
    }
  }

  /** Auth tokens — localStorage so all tabs / duplicates share one session. */
  private get storage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? localStorage : null;
  }

  /** Per-tab cache only; kept in sync for older code paths. */
  private get sessionTabStorage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? sessionStorage : null;
  }

  private get persistentStorage(): Storage | null {
    return this.storage;
  }

  login(request: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.apiService.post<LoginResponse>('Auth/login', request).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.persistAuthResponse(response.data);
          this.accessRightService.refreshAppModulesFromApi();
          this.startSessionExpiryWatch();
        }
      }),
    );
  }

  logout(): void {
    this.finalizeLogout(false);
  }

  logoutWithRevoke(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      this.apiService
        .post<boolean>('Auth/logout', { refreshToken })
        .subscribe({ error: () => undefined });
    }
    this.handlingSessionExpiry = false;
    this.finalizeLogout(true);
  }

  /** Exchange refresh token for a new access + refresh token pair. */
  extendSession(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshTokenAtStart = this.getRefreshToken();
    if (!refreshTokenAtStart || this.isRefreshTokenExpired()) {
      return of(false);
    }

    const accessExpAtStart = this.getTokenExpiryMs(this.getToken() ?? '');

    if (!this.acquireRefreshLock()) {
      this.refreshInFlight$ = this.waitForSharedRefresh(
        refreshTokenAtStart,
        accessExpAtStart,
      ).pipe(
        switchMap((adopted) => {
          if (adopted) {
            return of(true);
          }
          if (!this.canRefreshSession()) {
            return of(false);
          }
          return this.extendSession();
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.apiService
      .post<LoginResponse>('Auth/refresh', { refreshToken: refreshTokenAtStart })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.persistAuthResponse(response.data);
            this.sessionExpiryService.close();
            this.handlingSessionExpiry = false;
            this.startSessionExpiryWatch();
          }
        }),
        map((response) => response.success === true && !!response.data),
        catchError((err: unknown) => {
          if (this.isTransientRefreshError(err)) {
            return of(false);
          }
          if (
            this.hasSharedSessionAdvanced(refreshTokenAtStart, accessExpAtStart)
          ) {
            return of(true);
          }
          if (this.getRefreshToken() === refreshTokenAtStart) {
            this.dropRefreshTokenFromSession();
          }
          return of(false);
        }),
        tap((success) => {
          if (success) {
            return;
          }
          if (
            this.hasSharedSessionAdvanced(refreshTokenAtStart, accessExpAtStart)
          ) {
            return;
          }
          if (
            this.getRefreshToken() === refreshTokenAtStart &&
            !this.isRefreshTokenExpired()
          ) {
            this.dropRefreshTokenFromSession();
          }
        }),
        finalize(() => {
          this.releaseRefreshLock();
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.refreshInFlight$;
  }

  /** Clear revoked/invalid refresh token without wiping the still-valid access JWT. */
  private dropRefreshTokenFromSession(): void {
    const session = this.readJwtSession();
    if (!session?.token) {
      return;
    }
    this.writeJwtSession({
      token: session.token,
      refreshToken: undefined,
      refreshTokenExpiresAt: undefined,
    });
  }

  getRefreshToken(): string | null {
    return this.getAuthData()?.refreshToken ?? null;
  }

  canRefreshSession(): boolean {
    return !!this.getRefreshToken() && !this.isRefreshTokenExpired();
  }

  isRefreshTokenExpired(): boolean {
    const expiresAt = this.getAuthData()?.refreshTokenExpiresAt;
    if (!expiresAt) {
      // Missing expiry metadata — still allow refresh; server validates.
      return false;
    }
    const expiryMs = this.parseUtcDateMs(expiresAt);
    return !Number.isFinite(expiryMs) || expiryMs <= Date.now();
  }

  /** Parse API UTC datetime (with or without Z/offset) to epoch ms. */
  private parseUtcDateMs(value: string): number {
    const raw = value.trim();
    if (!raw) return Number.NaN;
    if (/[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw)) {
      return Date.parse(raw);
    }
    const iso = raw.includes('T') ? raw : `${raw}T00:00:00`;
    return Date.parse(`${iso}Z`);
  }

  /** Access token expired but refresh may still extend the session. */
  hasExpiredAccessToken(): boolean {
    const token = this.getToken();
    return !!token && this.isTokenExpired(token);
  }

  /** No valid access token and no refresh path left. */
  hasExpiredSession(): boolean {
    if (!this.getAuthData()?.token) {
      return true;
    }
    if (!this.hasExpiredAccessToken()) {
      return false;
    }
    return !this.canRefreshSession();
  }

  /** Force logout when session cannot be extended — clears storage and redirects to login. */
  handleSessionExpired(message = 'Please sign in again.'): void {
    if (this.handlingSessionExpiry) {
      return;
    }
    this.handlingSessionExpiry = true;
    this.finalizeLogout(true, message);
  }

  /** Re-check session on navigation / poll — silently renew when possible. */
  checkAndHandleExpiredSession(): void {
    if (!this.getAuthData()?.token) {
      return;
    }

    if (!this.hasExpiredAccessToken()) {
      return;
    }

    if (this.canRefreshSession()) {
      this.trySilentExtendSession();
      return;
    }

    this.handleSessionExpired();
  }

  /**
   * Renew access JWT via refresh token without forcing logout.
   * Used by proactive timer, expiry poll, interceptor, and the expiry modal.
   */
  trySilentExtendSession(): void {
    if (!this.canRefreshSession()) {
      if (this.hasExpiredAccessToken()) {
        this.handleSessionExpired();
      }
      return;
    }
    if (this.refreshInFlight$) {
      return;
    }

    this.extendSession().subscribe({
      next: (success) => {
        if (!success && this.hasExpiredAccessToken()) {
          this.handleSessionExpired(
            'Unable to extend your session. Please sign in again.',
          );
        }
      },
      error: () => {
        if (this.hasExpiredAccessToken()) {
          this.handleSessionExpired(
            'Unable to extend your session. Please sign in again.',
          );
        }
      },
    });
  }

  /** Schedule silent refresh + soft warning before access token expiry. */
  startSessionExpiryWatch(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.stopSessionExpiryWatch();
    this.handlingSessionExpiry = false;

    const token = this.getToken();
    if (!token) {
      return;
    }

    if (this.hasExpiredAccessToken()) {
      this.checkAndHandleExpiredSession();
      return;
    }

    const payload = this.decodeJwtPayload(token);
    const expiryInMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expiryInMs)) {
      return;
    }

    const now = Date.now();
    const msUntilExpiry = expiryInMs - now;

    // Renew a few minutes before access JWT dies (never force logout while refresh is valid).
    const refreshLeadMs = Math.min(
      AuthService.SILENT_REFRESH_LEAD_MS,
      Math.max(30_000, msUntilExpiry - 5_000),
    );
    const refreshDelayMs = Math.max(0, msUntilExpiry - refreshLeadMs);

    this.sessionRefreshTimer = setTimeout(() => {
      this.ngZone.run(() => this.trySilentExtendSession());
    }, refreshDelayMs);

    // Optional soft warning; if ignored, silent refresh still runs above.
    const warningLeadMs = Math.min(
      this.sessionWarningMs,
      Math.max(5_000, msUntilExpiry - 1_000),
    );
    const warnDelayMs = Math.max(0, msUntilExpiry - warningLeadMs);

    this.sessionWarningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (
          !this.sessionExpiryService.visible() &&
          this.canRefreshSession() &&
          !this.hasExpiredAccessToken()
        ) {
          this.openSessionWarning(this.getSecondsUntilAccessExpiry());
        }
      });
    }, warnDelayMs);

    this.sessionExpiryTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.hasExpiredAccessToken() || this.sessionExpiryService.visible()) {
          this.trySilentExtendSession();
        }
      });
    }, Math.max(0, msUntilExpiry + 50));

    this.sessionCheckInterval = setInterval(() => {
      this.ngZone.run(() => this.checkAndHandleExpiredSession());
    }, AuthService.SESSION_POLL_MS);

    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        this.ngZone.run(() => this.checkAndHandleExpiredSession());
      });
  }

  stopSessionExpiryWatch(): void {
    if (this.sessionWarningTimer !== null) {
      clearTimeout(this.sessionWarningTimer);
      this.sessionWarningTimer = null;
    }
    if (this.sessionRefreshTimer !== null) {
      clearTimeout(this.sessionRefreshTimer);
      this.sessionRefreshTimer = null;
    }
    if (this.sessionExpiryTimer !== null) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = null;
    }
    if (this.sessionCheckInterval !== null) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }

  private openSessionWarning(secondsRemaining: number): void {
    if (!this.canRefreshSession()) {
      if (!this.hasExpiredAccessToken()) {
        return;
      }
      this.handleSessionExpired();
      return;
    }
    this.sessionExpiryService.open(Math.max(1, secondsRemaining), () => {
      this.trySilentExtendSession();
    });
  }

  private getSecondsUntilAccessExpiry(): number {
    const token = this.getToken();
    if (!token) {
      return 0;
    }
    const payload = this.decodeJwtPayload(token);
    const expiryInMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expiryInMs)) {
      return 0;
    }
    return Math.max(0, Math.ceil((expiryInMs - Date.now()) / 1_000));
  }

  private persistAuthResponse(data: LoginResponse): void {
    const envelope = data as LoginResponse & Record<string, unknown>;
    const rightsRaw = data.accessRights ?? envelope['AccessRights'];
    this.memoryAccessRights = this.normalizeAccessRights(
      Array.isArray(rightsRaw) ? rightsRaw : [],
    );
    const refreshExpires =
      data.refreshTokenExpiresAtUtc ??
      (envelope['RefreshTokenExpiresAtUtc'] as string | undefined);
    const accessToken =
      data.token ?? (envelope['Token'] as string | undefined) ?? '';
    const refreshToken =
      data.refreshToken ?? (envelope['RefreshToken'] as string | undefined);
    this.writeJwtSession({
      token: accessToken,
      refreshToken,
      refreshTokenExpiresAt: refreshExpires,
      accessRights: this.memoryAccessRights,
    });
    this.applyRecentLoginCompany(data);
  }

  private applyRecentLoginCompany(data: LoginResponse): void {
    const envelope = data as LoginResponse & Record<string, unknown>;
    const raw = envelope.lastAccountCompanyId ?? envelope['LastAccountCompanyId'];
    const fromDto =
      typeof raw === 'string'
        ? raw.trim()
        : raw != null
          ? String(raw).trim()
          : '';
    const companyId = fromDto || DEFAULT_ACCOUNTING_COMPANY_ID;
    this.accountCompanyService.setActiveCompanyId(companyId);
  }

  private writeJwtSession(session: JwtSessionStore): void {
    const storage = this.storage;
    if (!storage) return;
    const payload = JSON.stringify({
      token: session.token,
      refreshToken: session.refreshToken,
      refreshTokenExpiresAt: session.refreshTokenExpiresAt,
      accessRights: session.accessRights,
    } satisfies JwtSessionStore);
    storage.setItem(STORAGE_KEY, payload);
    this.sessionTabStorage?.setItem(STORAGE_KEY, payload);
  }

  private readJwtSession(): JwtSessionStore | null {
    const storage = this.storage;
    if (!storage) return null;

    const localRaw = storage.getItem(STORAGE_KEY);
    const fromLocal = this.parseSessionRaw(localRaw);
    if (fromLocal) {
      if (localRaw && this.sessionRawContainsUserDetails(localRaw)) {
        this.writeJwtSession(fromLocal);
      }
      return fromLocal;
    }

    const sessionRaw = this.sessionTabStorage?.getItem(STORAGE_KEY) ?? null;
    const fromSession = this.parseSessionRaw(sessionRaw);
    if (fromSession) {
      this.writeJwtSession(fromSession);
      return fromSession;
    }

    return null;
  }

  private sessionRawContainsUserDetails(raw: string): boolean {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return (
        'emailId' in parsed ||
        'mobileNo' in parsed ||
        'accessRights' in parsed ||
        'user' in parsed
      );
    } catch {
      return true;
    }
  }

  private parseSessionRaw(raw: string | null): JwtSessionStore | null {
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object' && typeof parsed['token'] === 'string') {
        const token = parsed['token'] as string;
        // Legacy nested user / rights — keep tokens only; rights must rehydrate via refresh.
        if ('user' in parsed && parsed['user'] && typeof parsed['user'] === 'object') {
          const user = parsed['user'] as Record<string, unknown>;
          if (Array.isArray(user['accessRights']) && this.memoryAccessRights.length === 0) {
            this.memoryAccessRights = this.normalizeAccessRights(
              user['accessRights'] as unknown[],
            );
          }
        } else if (
          Array.isArray(parsed['accessRights']) &&
          this.memoryAccessRights.length === 0
        ) {
          this.memoryAccessRights = this.normalizeAccessRights(
            parsed['accessRights'] as unknown[],
          );
        }

        if (
          Array.isArray(parsed['accessRights']) &&
          this.memoryAccessRights.length === 0
        ) {
          this.memoryAccessRights = this.normalizeAccessRights(
            parsed['accessRights'] as unknown[],
          );
        }

        return {
          token,
          refreshToken:
            typeof parsed['refreshToken'] === 'string'
              ? parsed['refreshToken']
              : undefined,
          refreshTokenExpiresAt:
            typeof parsed['refreshTokenExpiresAt'] === 'string'
              ? parsed['refreshTokenExpiresAt']
              : undefined,
          accessRights: this.memoryAccessRights.length
            ? this.memoryAccessRights
            : undefined,
        };
      }
    } catch {
      // may be legacy AES blob
    }

    const legacy = this.encryption.decrypt<AuthStoragePayload>(raw);
    if (legacy?.token) {
      if (legacy.user?.accessRights?.length && this.memoryAccessRights.length === 0) {
        this.memoryAccessRights = this.normalizeAccessRights(legacy.user.accessRights);
      }
      return {
        token: legacy.token,
        refreshToken: legacy.refreshToken,
        refreshTokenExpiresAt: legacy.refreshTokenExpiresAt,
      };
    }

    return null;
  }

  /** One-time migration from sessionStorage / legacy sj_auth_sync to localStorage sj_auth. */
  private migrateLegacyAuthStorage(): void {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    if (storage.getItem(STORAGE_KEY)) {
      storage.removeItem(AuthService.LEGACY_AUTH_SYNC_KEY);
      return;
    }

    const legacySyncRaw = storage.getItem(AuthService.LEGACY_AUTH_SYNC_KEY);
    if (legacySyncRaw) {
      try {
        const sync = JSON.parse(legacySyncRaw) as JwtSessionStore & {
          accessRights?: AccessRightDto[];
        };
        if (sync?.token) {
          if (Array.isArray(sync.accessRights) && sync.accessRights.length > 0) {
            this.memoryAccessRights = this.normalizeAccessRights(sync.accessRights);
          }
          this.writeJwtSession({
            token: sync.token,
            refreshToken: sync.refreshToken,
            refreshTokenExpiresAt: sync.refreshTokenExpiresAt,
            accessRights: this.memoryAccessRights,
          });
        }
      } catch {
        // ignore malformed legacy mirror
      }
      storage.removeItem(AuthService.LEGACY_AUTH_SYNC_KEY);
      return;
    }

    const sessionRaw = this.sessionTabStorage?.getItem(STORAGE_KEY) ?? null;
    const fromSession = this.parseSessionRaw(sessionRaw);
    if (fromSession) {
      this.writeJwtSession(fromSession);
    }
  }

  private hasSharedSessionAdvanced(
    refreshTokenAtStart: string,
    accessExpAtStart: number,
  ): boolean {
    const session = this.readJwtSession();
    if (!session?.token) {
      return false;
    }
    if (session.refreshToken && session.refreshToken !== refreshTokenAtStart) {
      this.sessionExpiryService.close();
      this.handlingSessionExpiry = false;
      this.startSessionExpiryWatch();
      return true;
    }
    const exp = this.getTokenExpiryMs(session.token);
    if (exp > accessExpAtStart + 1000) {
      this.sessionExpiryService.close();
      this.handlingSessionExpiry = false;
      this.startSessionExpiryWatch();
      return true;
    }
    return false;
  }

  private waitForSharedRefresh(
    refreshTokenAtStart: string,
    accessExpAtStart: number,
  ): Observable<boolean> {
    const startedAt = Date.now();
    const pollMs = 400;

    return new Observable<boolean>((subscriber) => {
      const tick = () => {
        if (this.hasSharedSessionAdvanced(refreshTokenAtStart, accessExpAtStart)) {
          subscriber.next(true);
          subscriber.complete();
          return;
        }
        if (Date.now() - startedAt >= AuthService.PEER_REFRESH_WAIT_MS) {
          subscriber.next(false);
          subscriber.complete();
          return;
        }
        timer(pollMs).subscribe(() => tick());
      };
      tick();
    });
  }

  private acquireRefreshLock(): boolean {
    const storage = this.persistentStorage;
    if (!storage) {
      return true;
    }

    const now = Date.now();
    const raw = storage.getItem(AuthService.REFRESH_LOCK_KEY);
    if (raw) {
      try {
        const lock = JSON.parse(raw) as { tabId?: string; until?: number };
        if (
          typeof lock.until === 'number' &&
          lock.until > now &&
          lock.tabId !== this.tabId
        ) {
          return false;
        }
      } catch {
        // overwrite malformed lock
      }
    }

    storage.setItem(
      AuthService.REFRESH_LOCK_KEY,
      JSON.stringify({ tabId: this.tabId, until: now + AuthService.REFRESH_LOCK_MS }),
    );
    return true;
  }

  private releaseRefreshLock(): void {
    const storage = this.persistentStorage;
    if (!storage) {
      return;
    }
    const raw = storage.getItem(AuthService.REFRESH_LOCK_KEY);
    if (!raw) {
      return;
    }
    try {
      const lock = JSON.parse(raw) as { tabId?: string };
      if (lock.tabId === this.tabId) {
        storage.removeItem(AuthService.REFRESH_LOCK_KEY);
      }
    } catch {
      storage.removeItem(AuthService.REFRESH_LOCK_KEY);
    }
  }

  private isTransientRefreshError(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    return status === 0 || (typeof status === 'number' && status >= 500);
  }

  private getTokenExpiryMs(token: string): number {
    const payload = this.decodeJwtPayload(token);
    const expiryInMs = Number(payload?.exp) * 1000;
    return Number.isFinite(expiryInMs) ? expiryInMs : 0;
  }

  private finalizeLogout(
    redirectToLogin: boolean,
    sessionExpiredMessage?: string,
  ): void {
    this.stopSessionExpiryWatch();
    this.sessionExpiryService.close();
    this.accessRightService.clearAppModulesCache();
    this.masterDataCache.clearAll();
    this.memoryAccessRights = [];
    this.clearAuthStorage();

    if (!redirectToLogin) {
      return;
    }

    this.ngZone.run(() => {
      if (sessionExpiredMessage) {
        this.toastService.warning('Session Expired', sessionExpiredMessage);
      }
      void this.router.navigate(['/login'], { replaceUrl: true });
    });
  }

  /** Remove auth keys only — never localStorage.clear() (wipes UI prefs and other tabs). */
  private clearAuthStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(AuthService.LEGACY_AUTH_SYNC_KEY);
    localStorage.removeItem(AuthService.REFRESH_LOCK_KEY);
    localStorage.removeItem(ACCOUNTING_COMPANY_SESSION_KEY);
    localStorage.removeItem(ACCOUNTING_COMPANY_OPEN_SESSION_KEY);

    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(ACCOUNTING_COMPANY_SESSION_KEY);
    sessionStorage.removeItem(ACCOUNTING_COMPANY_OPEN_SESSION_KEY);
  }

  /**
   * Tokens from localStorage (shared across tabs).
   * Identity/role decoded from JWT in memory; accessRights kept in storage + memory.
   */
  getAuthData(): AuthStoragePayload | null {
    const session = this.readJwtSession();
    if (!session?.token) return null;

    const claims = this.decodeJwtPayload(session.token);
    const accessRights = this.memoryAccessRights;
    const user = this.userFromJwtClaims(claims, accessRights);

    return {
      token: session.token,
      refreshToken: session.refreshToken,
      refreshTokenExpiresAt: session.refreshTokenExpiresAt,
      accessRights,
      user,
    };
  }

  /**
   * After page reload, rights are not in storage — reload via refresh token once.
   */
  ensureAccessRightsLoaded(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(true);
    }
    if (!this.isLoggedIn()) {
      return of(false);
    }
    if (this.memoryAccessRights.length > 0) {
      return of(true);
    }
    if (!this.canRefreshSession()) {
      if (!this.hasExpiredAccessToken()) {
        return of(true);
      }
      this.handleSessionExpired('Please sign in again.');
      return of(false);
    }
    return this.extendSession().pipe(
      tap((ok) => {
        if (!ok && this.hasExpiredAccessToken()) {
          this.handleSessionExpired('Please sign in again.');
        }
      }),
    );
  }

  getToken(): string | null {
    return this.readJwtSession()?.token ?? null;
  }

  /** Returns the numeric dbo.Role.RoleId from JWT claims. */
  getUserType(): number | null {
    const claims = this.decodeJwtPayload(this.getToken() ?? '');
    if (!claims) return null;
    const raw = claims.UserRole ?? claims.role ?? claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    const roleParsed = Number(raw);
    return Number.isFinite(roleParsed) ? roleParsed : null;
  }

  /** dbo.Role display name from JWT claim UserRoleName. */
  getUserRoleName(): string | null {
    const claims = this.decodeJwtPayload(this.getToken() ?? '');
    const named = String(claims?.UserRoleName ?? '').trim();
    return named || null;
  }

  /** True when dbo.Role is Admin (by JWT name or RoleId). Not AccessRights. */
  isDbAdminRole(): boolean {
    const name = this.getUserRoleName()?.toLowerCase();
    if (name === 'admin') return true;
    return this.getUserType() === 1;
  }

  isTokenExpired(token: string | null = this.getToken()): boolean {
    if (!token) return true;
    const payload = this.decodeJwtPayload(token);

    if (!payload?.exp) return false;
    const expiryInMs = Number(payload.exp) * 1000;
    return Number.isFinite(expiryInMs) && expiryInMs <= Date.now();
  }

  isLoggedIn(): boolean {
    const auth = this.getAuthData();
    if (!auth?.token) {
      return false;
    }
    if (!this.isTokenExpired(auth.token)) {
      return true;
    }
    return this.canRefreshSession();
  }

  private userFromJwtClaims(
    claims: JwtAccessClaims | null,
    accessRights: AccessRightDto[],
  ): AuthStoragePayload['user'] {
    const nameId =
      claims?.sub ??
      claims?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
    const userName =
      String(
        claims?.unique_name ??
          claims?.name ??
          claims?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ??
          '',
      ).trim() || 'User';
    const email = String(
      claims?.email ??
        claims?.Email ??
        claims?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ??
        '',
    ).trim();
    const mobile = String(claims?.MobileNo ?? '').trim();
    const roleRaw = claims?.UserRole ?? claims?.role;
    const roleNum = Number(roleRaw);
    const roleName = String(claims?.UserRoleName ?? '').trim() || undefined;

    return {
      userId: String(nameId ?? ''),
      userName,
      emailId: email,
      mobileNo: mobile,
      userRole: Number.isFinite(roleNum) ? roleNum : (roleRaw as string | number) ?? '',
      userRoleName: roleName,
      accessRights,
    };
  }

  decodeJwtPayload(token: string): JwtAccessClaims | null {
    if (!token?.trim()) return null;
    try {
      const payload = token.split('.')[1];

      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '=',
      );
      return JSON.parse(globalThis.atob(padded)) as JwtAccessClaims;
    } catch {
      return null;
    }
  }

  /** Map API / legacy stored rights to appModuleName-based DTOs. */

  normalizeAccessRights(raw: unknown[] | null | undefined): AccessRightDto[] {
    return (raw ?? []).map((item) => {
      const r = item as Record<string, unknown>;

      const appModuleName = String(
        r['appModuleName'] ?? r['moduleName'] ?? r['AppModuleName'] ?? '',
      ).trim();

      const moduleId =
        r['moduleId'] != null ? String(r['moduleId']) : undefined;

      return {
        appModuleName,
        moduleId,
        moduleName:
          appModuleName ||
          (r['moduleName'] != null ? String(r['moduleName']) : undefined),
        canView: Boolean(r['canView']),
        canAdd: Boolean(r['canAdd']),
        canEdit: Boolean(r['canEdit']),
        canDelete: Boolean(r['canDelete']),
        canExport: r['canExport'] != null ? Boolean(r['canExport']) : false,
      };
    });
  }

  private moduleNameFromRight(r: AccessRightDto): string {
    return (r.appModuleName || r.moduleName || '').trim();
  }

  /** Get access for a module by AppModuleName (normalized match). */

  getAccessForModuleName(appModuleName: string): AccessRightDto | undefined {
    const key = normalizeModuleKey(appModuleName);
    if (!key) return undefined;
    const rights = this.getAuthData()?.user?.accessRights ?? [];
    return rights.find(
      (r) => normalizeModuleKey(this.moduleNameFromRight(r)) === key,
    );
  }

  /** @deprecated Use getAccessForModuleName. Resolves by name first, then legacy moduleId. */

  getAccessForModule(moduleIdOrName: string): AccessRightDto | undefined {
    const byName = this.getAccessForModuleName(moduleIdOrName);
    if (byName) return byName;
    const rights = this.getAuthData()?.user?.accessRights ?? [];
    return rights.find(
      (r) => r.moduleId?.toLowerCase() === moduleIdOrName?.toLowerCase(),
    );
  }

  arePermissionsReady(): boolean {
    return isPlatformBrowser(this.platformId) && this.isLoggedIn();
  }
  private hasNoAccessRightsDefined(): boolean {
    if (!this.arePermissionsReady()) {
      return false;
    }
    const rights = this.getAuthData()?.user?.accessRights;
    return !rights || rights.length === 0;
  }

  getModuleNameByPath(path: string): string | null {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const exact = MODULE_NAMES_BY_PATH[normalized];
    if (exact) return exact;

    let best: string | null = null;
    let bestPrefixLen = 0;
    for (const [prefix, moduleName] of Object.entries(MODULE_NAMES_BY_PATH)) {
      if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
        if (prefix.length > bestPrefixLen) {
          bestPrefixLen = prefix.length;
          best = moduleName;
        }
      }
    }
    return best;
  }

  hasViewAccess(appModuleName: string): boolean {
    if (this.hasNoAccessRightsDefined()) return true;
    const access = this.getAccessForModuleName(appModuleName);
    return access?.canView === true;
  }

  hasAddAccess(appModuleName: string): boolean {
    if (this.hasNoAccessRightsDefined()) return true;
    const access = this.getAccessForModuleName(appModuleName);
    return access?.canAdd === true;
  }

  hasEditAccess(appModuleName: string): boolean {
    if (this.hasNoAccessRightsDefined()) return true;
    const access = this.getAccessForModuleName(appModuleName);
    return access?.canEdit === true;
  }

  hasDeleteAccess(appModuleName: string): boolean {
    if (this.hasNoAccessRightsDefined()) return true;
    const access = this.getAccessForModuleName(appModuleName);
    return access?.canDelete === true;
  }

  hasExportAccess(appModuleName: string): boolean {
    if (this.hasNoAccessRightsDefined()) return true;
    const access = this.getAccessForModuleName(appModuleName);
    return access?.canExport === true;
  }

  private isAccountingPermissionBypassPath(path: string): boolean {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return (
      normalized === '/accounting' ||
      normalized.startsWith('/accounting/') ||
      normalized === '/item-wise-profit-loss' ||
      normalized.startsWith('/item-wise-profit-loss/') ||
      normalized === '/stock-report' ||
      normalized.startsWith('/stock-report/')
    );
  }

  hasViewAccessByPath(path: string): boolean {
    if (this.isAccountingPermissionBypassPath(path)) {
      return true;
    }
    const appModuleName = this.getModuleNameByPath(path);
    if (appModuleName == null) {
      return UNGATED_VIEW_PATHS.has(path);
    }
    if (!this.arePermissionsReady()) {
      return false;
    }
    if (this.hasNoAccessRightsDefined()) {
      return true;
    }
    return this.hasViewAccess(appModuleName);
  }

  hasAddAccessByPath(path: string): boolean {
    if (this.isAccountingPermissionBypassPath(path)) {
      return true;
    }
    const appModuleName = this.getModuleNameByPath(path);
    if (appModuleName == null) return false;
    if (this.hasNoAccessRightsDefined()) return true;
    return this.hasAddAccess(appModuleName);
  }

  hasEditAccessByPath(path: string): boolean {
    if (this.isAccountingPermissionBypassPath(path)) {
      return true;
    }
    const appModuleName = this.getModuleNameByPath(path);
    if (appModuleName == null) return false;
    if (this.hasNoAccessRightsDefined()) return true;
    return this.hasEditAccess(appModuleName);
  }

  /** True when the role has Edit on the Edit Accounting module (alter flows). */
  hasAccountingAlterAccess(): boolean {
    return true;
    // if (this.hasNoAccessRightsDefined()) return true;
    // return this.hasEditAccess(ACCOUNTING_EDIT_MODULE_NAME);
  }

  /** True when the role may delete vouchers (Voucher Entry module — Day Book Alt+D, etc.). */
  hasVoucherEntryDeleteAccess(): boolean {
    return true;
    // if (this.hasNoAccessRightsDefined()) return true;
    // return this.hasDeleteAccess(ACCOUNTING_VOUCHER_ENTRY_MODULE_NAME);
  }

  hasDeleteAccessByPath(path: string): boolean {
    if (this.isAccountingPermissionBypassPath(path)) {
      return true;
    }
    const appModuleName = this.getModuleNameByPath(path);
    if (appModuleName == null) return false;
    if (this.hasNoAccessRightsDefined()) return true;
    return this.hasDeleteAccess(appModuleName);
  }
}
